import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Video, Building2, Phone, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, fmtDate, fmtTime, errMsg } from "@/lib/api";
import { STATUS_STYLE } from "@/pages/MyAppointments";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const Detail = ({ b, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({ status: b.status, meeting_link: b.meeting_link || "", admin_notes: b.admin_notes || "" });
  const save = async () => {
    try { await api("patch", `/admin/bookings/${b.id}`, f); toast.success("Booking updated"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try { await api("delete", `/admin/bookings/${b.id}`); toast.success("Deleted"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DialogContent className="max-w-lg rounded-3xl" data-testid="admin-booking-dialog">
      <DialogHeader><DialogTitle className="font-display text-2xl">{b.child_name} <span className="text-base text-slate-400">· {b.ref}</span></DialogTitle></DialogHeader>
      <div className="space-y-1 text-sm text-slate-600">
        <p><b>{b.service}</b> · age {b.child_age} · {b.type === "online" ? "Online consult" : "Clinic visit"}</p>
        <p>{fmtDate(b.date)} at {fmtTime(b.time)}</p>
        <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /><a href={`tel:${b.phone}`} className="underline">{b.phone}</a> · {b.parent_name}</p>
        <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{b.email}</p>
        {b.notes && <p className="rounded-xl bg-slate-50 p-3">"{b.notes}"</p>}
      </div>
      <div className="space-y-3">
        <Select value={f.status} onValueChange={(status) => setF({ ...f, status })}>
          <SelectTrigger data-testid="admin-booking-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        {b.type === "online" && (
          <Input placeholder="https://meet.google.com/..." value={f.meeting_link} onChange={(e) => setF({ ...f, meeting_link: e.target.value })} data-testid="admin-meeting-link-input" />
        )}
        <Textarea rows={2} placeholder="Private clinic notes" value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} data-testid="admin-notes-input" />
        <p className="text-xs text-slate-400">Changing status to confirmed or cancelled emails the parent{b.type === "online" ? " (with the meeting link)" : ""}.</p>
      </div>
      <div className="flex justify-between">
        <button onClick={remove} className="text-sm font-bold text-rose-600" data-testid="admin-booking-delete">Delete</button>
        <button onClick={save} className="rounded-full bg-teal-600 px-6 py-2.5 text-sm font-bold text-white" data-testid="admin-booking-save">Save changes</button>
      </div>
    </DialogContent>
  );
};

const Filter = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-40 bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const AdminBookings = () => {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    if (type !== "all") p.set("type", type);
    if (q) p.set("q", q);
    api("get", `/admin/bookings?${p}`).then(setItems).catch(() => {});
  }, [api, status, type, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input className="bg-white pl-9" placeholder="Search child, parent, phone, ref" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-bookings-search" /></div>
        <Filter value={status} onChange={setStatus} testid="admin-filter-status" options={[["all", "All statuses"], ...STATUSES.map((s) => [s, s[0].toUpperCase() + s.slice(1)])]} />
        <Filter value={type} onChange={setType} testid="admin-filter-type" options={[["all", "All types"], ["clinic", "Clinic"], ["online", "Online"]]} />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-bookings-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-3">When</th><th className="p-3">Child</th><th className="p-3">Service</th><th className="p-3">Parent</th><th className="p-3">Type</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No bookings found.</td></tr>}
            {items.map((b) => (
              <tr key={b.id} onClick={() => setOpen(b)} className="cursor-pointer border-t border-slate-100 hover:bg-teal-50/50" data-testid="admin-booking-row">
                <td className="p-3 font-semibold text-slate-800">{fmtDate(b.date)}<br /><span className="font-normal text-slate-500">{fmtTime(b.time)}</span></td>
                <td className="p-3">{b.child_name}<br /><span className="text-slate-500">{b.child_age}</span></td>
                <td className="p-3">{b.service}</td>
                <td className="p-3">{b.parent_name}<br /><span className="text-slate-500">{b.phone}</span></td>
                <td className="p-3">{b.type === "online" ? <Video className="h-4 w-4 text-violet-600" /> : <Building2 className="h-4 w-4 text-rose-500" />}</td>
                <td className="p-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold capitalize ${STATUS_STYLE[b.status]}`}>{b.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        {open && <Detail b={open} onClose={() => setOpen(null)} onSaved={load} />}
      </Dialog>
    </div>
  );
};
